import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { finalize, take } from 'rxjs';
import { ToastService } from '../../core/services/toast.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { PdfViewerDialogComponent } from '../../shared/components/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { TermoDetalhePanelComponent } from './components/termo-detalhe-panel.component';
import { TermoVinculosPanelComponent } from './components/termo-vinculos-panel.component';
import {
  Termo,
  TermoArquivoUpload,
  TermoArquivoSolicitado,
  TermoArquivoVisualizacao,
  TermoFiltros,
  TermoEdicao,
  TermoNovo,
  TermoResumo,
  TermoStatus,
} from './models/termo.model';
import { TermoService } from './services/termo.service';

const PAGE_SIZE = 6;
const STATUS_TODOS = 'todos';

/** Função, e não constante, para cada "Novo Termo" chegar como objeto novo e limpar o formulário. */
function rascunhoEmBranco(): Termo {
  return {
    id: 0,
    nome: '',
    descricao: '',
    versao: '',
    dataVersao: '',
    status: 'Ativo',
    arquivo: null,
    historico: [],
    funcionalidades: [],
  };
}

/**
 * Página de Gestão de Termos: lista, detalhe e vínculos. Faz as chamadas à API e
 * controla loader, toasts e diálogos; os painéis só emitem eventos.
 */
@Component({
  selector: 'app-termos-list',
  standalone: true,
  imports: [
    PageHeaderComponent,
    AlertDialogComponent,
    EmptyStateComponent,
    PaginationComponent,
    PdfViewerDialogComponent,
    StatusBadgeComponent,
    TermoDetalhePanelComponent,
    TermoVinculosPanelComponent,
  ],
  templateUrl: './termos-list.component.html',
  styleUrl: './termos-list.component.scss',
})
export class TermosListComponent implements OnInit {
  private readonly termoService = inject(TermoService);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);

  private readonly termosSignal = signal<readonly TermoResumo[]>([]);
  private readonly totalItensSignal = signal(0);
  private readonly termoSelecionadoId = signal<number | null>(null);
  private readonly termoDetalhe = signal<Termo | null>(null);

  /** Termo em branco da criação; `null` fora dela. */
  private readonly rascunho = signal<Termo | null>(null);

  readonly termos = this.termosSignal.asReadonly();
  readonly totalItens = this.totalItensSignal.asReadonly();
  readonly currentPage = signal(1);
  readonly busca = signal('');
  readonly status = signal(STATUS_TODOS);

  // Dados aguardando confirmação no diálogo; cancelar não perde o formulário.
  private readonly criacaoPendente = signal<TermoNovo | null>(null);
  private readonly edicaoPendente = signal<TermoEdicao | null>(null);
  private readonly vinculosPendentes = signal<readonly number[] | null>(null);

  readonly showCriarDialog = computed(() => this.criacaoPendente() !== null);
  readonly showSalvarDialog = computed(() => this.edicaoPendente() !== null);
  readonly showVinculosDialog = computed(() => this.vinculosPendentes() !== null);
  readonly showRemoverArquivoDialog = signal(false);

  /** Textos do diálogo de alteração: Inativar, Ativar ou Salvar, conforme o que mudou. */
  readonly dialogoSalvar = computed(() => {
    const valores = this.edicaoPendente();
    const termo = this.termoSelecionado();
    const mudouStatus = !!valores && !!termo && valores.status !== termo.status;
    const outrasAlteracoes =
      !!valores &&
      !!termo &&
      (valores.nome !== termo.nome ||
        valores.descricao !== termo.descricao ||
        valores.arquivo !== null);
    const complemento = outrasAlteracoes ? ' As demais alterações do termo também são salvas.' : '';

    if (mudouStatus && valores.status === 'Inativo') {
      return {
        title: 'Inativar Termo',
        message: 'Tem certeza que deseja inativar este termo?',
        description: `Essa ação impacta as funcionalidades vinculadas a este termo: ele deixa de ser exibido nelas. Adesões já concluídas não são afetadas, e você pode ativá-lo de novo quando quiser.${complemento}`,
        confirmLabel: 'Inativar Termo',
        sucesso: 'Termo inativado.',
        erro: 'Não foi possível inativar o termo.',
      };
    }

    if (mudouStatus) {
      return {
        title: 'Ativar Termo',
        message: 'Tem certeza que deseja ativar este termo?',
        description: `Essa ação impacta as funcionalidades vinculadas a este termo: ele volta a ser exibido nelas para quem ainda não o aceitou. Adesões já concluídas não são afetadas.${complemento}`,
        confirmLabel: 'Ativar Termo',
        sucesso: 'Termo ativado.',
        erro: 'Não foi possível ativar o termo.',
      };
    }

    return {
      title: 'Salvar Alterações',
      message: 'Tem certeza que deseja salvar as alterações deste termo?',
      description:
        'Essa ação impacta as funcionalidades vinculadas a este termo: a nova versão passa a ser exibida nelas para quem ainda não o aceitou. Adesões já concluídas não são afetadas.',
      confirmLabel: 'Salvar Alterações',
      sucesso: 'Termo salvo.',
      erro: 'Não foi possível salvar o termo.',
    };
  });

  /** Ligado ao `arquivoNovo` do painel. */
  readonly arquivoPendente = signal<TermoArquivoUpload | null>(null);

  readonly arquivoVisualizado = signal<TermoArquivoVisualizacao | null>(null);

  /** `blob:` do PDF baixado da API, liberado ao fechar o modal. */
  private urlBaixada: string | null = null;

  readonly pageSize = PAGE_SIZE;
  readonly formatDate = formatDateIsoToBr;

  readonly statusOptions = [
    { value: STATUS_TODOS, label: 'Todos' },
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Inativo', label: 'Inativo' },
  ];

  readonly termoSelecionado = this.termoDetalhe.asReadonly();

  readonly criandoTermo = computed(() => this.rascunho() !== null);

  readonly termoPainel = computed(() => this.rascunho() ?? this.termoSelecionado());

  ngOnInit(): void {
    this.carregarTermos();
  }

  buscar(): void {
    this.currentPage.set(1);
    this.carregarTermos();
  }

  limpar(): void {
    this.busca.set('');
    this.status.set(STATUS_TODOS);
    this.currentPage.set(1);
    this.carregarTermos();
  }

  onBuscaChange(valor: string): void {
    this.busca.set(valor);
  }

  onStatusChange(valor: string): void {
    this.status.set(valor);
  }

  selecionarTermo(id: number): void {
    this.abrirTermo(id);
  }

  abrirCriacao(): void {
    this.rascunho.set(rascunhoEmBranco());
  }

  descartarRascunho(): void {
    this.rascunho.set(null);
    this.criacaoPendente.set(null);
    this.arquivoPendente.set(null);
  }

  onCriarTermo(novo: TermoNovo): void {
    this.criacaoPendente.set(novo);
  }

  cancelarCriacaoTermo(): void {
    this.criacaoPendente.set(null);
  }

  confirmarCriacaoTermo(): void {
    const novo = this.criacaoPendente();
    this.criacaoPendente.set(null);

    if (!novo) {
      return;
    }

    this.loaderService.showLoader();
    this.termoService
      .criarTermo(novo)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (termo) => {
          this.rascunho.set(null);
          // Limpa os filtros para o termo criado aparecer na lista.
          this.busca.set('');
          this.status.set(STATUS_TODOS);
          this.currentPage.set(1);
          this.termoSelecionadoId.set(termo.id);
          this.carregarTermos();
          this.toastService.show('Termo criado.', 'success');
        },
        // O formulário continua preenchido para tentar de novo.
        error: () => this.toastService.show('Não foi possível criar o termo.', 'error'),
      });
  }

  onSalvarTermo(valores: TermoEdicao): void {
    if (this.termoSelecionado()) {
      this.edicaoPendente.set(valores);
    }
  }

  cancelarSalvarTermo(): void {
    this.edicaoPendente.set(null);
  }

  confirmarSalvarTermo(): void {
    const valores = this.edicaoPendente();
    const termo = this.termoSelecionado();
    // Lido antes de limpar a pendência, da qual os textos dependem.
    const { sucesso, erro } = this.dialogoSalvar();
    this.edicaoPendente.set(null);

    if (!valores || !termo) {
      return;
    }

    this.loaderService.showLoader();
    this.termoService
      .atualizarTermo(termo.id, valores)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.carregarTermos();
          this.toastService.show(sucesso, 'success');
        },
        error: () => this.toastService.show(erro, 'error'),
      });
  }

  /** Só chamado na criação; na alteração o painel remove o PDF sozinho. */
  onRemoverArquivo(): void {
    this.showRemoverArquivoDialog.set(true);
  }

  cancelarRemoverArquivo(): void {
    this.showRemoverArquivoDialog.set(false);
  }

  confirmarRemoverArquivo(): void {
    this.showRemoverArquivoDialog.set(false);

    const url = this.arquivoPendente()?.url;
    if (url) {
      URL.revokeObjectURL(url);
    }

    this.arquivoPendente.set(null);
  }

  /**
   * PDF da API é baixado antes de abrir: o modal não carrega a URL direto porque
   * a API exige o token no cabeçalho.
   */
  onVisualizarArquivo(arquivo: TermoArquivoSolicitado): void {
    if (arquivo.url) {
      this.arquivoVisualizado.set({ nome: arquivo.nome, url: arquivo.url });
      return;
    }

    const termo = this.termoSelecionado();

    if (!termo || arquivo.idVersao === undefined) {
      return;
    }

    this.loaderService.showLoader();
    this.termoService
      .getArquivo(termo.id, arquivo.idVersao)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (conteudo) => {
          this.urlBaixada = URL.createObjectURL(conteudo);
          this.arquivoVisualizado.set({ nome: arquivo.nome, url: this.urlBaixada });
        },
        error: () => this.toastService.show('Não foi possível abrir o PDF do termo.', 'error'),
      });
  }

  fecharVisualizacaoArquivo(): void {
    this.arquivoVisualizado.set(null);

    if (this.urlBaixada) {
      URL.revokeObjectURL(this.urlBaixada);
      this.urlBaixada = null;
    }
  }

  onArquivoRecusado(mensagem: string): void {
    this.toastService.show(mensagem, 'error');
  }

  onSalvarVinculos(idsVinculados: readonly number[]): void {
    if (this.termoSelecionado()) {
      this.vinculosPendentes.set(idsVinculados);
    }
  }

  cancelarSalvarVinculos(): void {
    this.vinculosPendentes.set(null);
  }

  confirmarSalvarVinculos(): void {
    const idsVinculados = this.vinculosPendentes();
    const termo = this.termoSelecionado();
    this.vinculosPendentes.set(null);

    if (!idsVinculados || !termo) {
      return;
    }

    this.loaderService.showLoader();
    this.termoService
      .atualizarVinculos(termo.id, idsVinculados)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.carregarTermos();
          this.toastService.show('Vínculos salvos.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar os vínculos.', 'error'),
      });
  }

  isSelecionado(id: number): boolean {
    return this.termoSelecionadoId() === id;
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.carregarTermos();
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  private carregarTermos(): void {
    this.loaderService.showLoader();
    this.termoService
      .getTermos(this.filtrosAtuais(), this.currentPage(), PAGE_SIZE)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (pagina) => {
          // A página pedida pode não existir mais: volta para a última.
          const paginaSumiu =
            pagina.itens.length === 0 &&
            pagina.totalPaginas > 0 &&
            this.currentPage() > pagina.totalPaginas;

          if (paginaSumiu) {
            this.currentPage.set(pagina.totalPaginas);
            this.carregarTermos();
            return;
          }

          this.termosSignal.set(pagina.itens);
          this.totalItensSignal.set(pagina.totalItens);
          this.manterSelecao();
        },
        error: () => {
          this.termosSignal.set([]);
          this.totalItensSignal.set(0);
          this.limparSelecao();
          this.toastService.show('Não foi possível carregar os termos.', 'error');
        },
      });
  }

  private filtrosAtuais(): TermoFiltros {
    const filtros: TermoFiltros = {};

    const nome = this.busca().trim();
    if (nome) {
      filtros.nome = nome;
    }

    if (this.status() !== STATUS_TODOS) {
      filtros.status = this.status() as TermoStatus;
    }

    return filtros;
  }

  /** Recarrega o termo selecionado se ainda está na página; senão, abre o primeiro. */
  private manterSelecao(): void {
    const termos = this.termos();
    const resumo = termos.find((termo) => termo.id === this.termoSelecionadoId()) ?? termos[0];

    if (resumo) {
      this.abrirTermo(resumo.id);
    } else {
      this.limparSelecao();
    }
  }

  private abrirTermo(id: number): void {
    this.termoSelecionadoId.set(id);
    this.loaderService.showLoader();
    this.termoService
      .getTermo(id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (termo) => {
          // Ignora respostas atrasadas de um termo que já não está selecionado.
          if (this.termoSelecionadoId() === termo.id) {
            this.termoDetalhe.set(termo);
          }
        },
        error: () => this.toastService.show('Não foi possível abrir o termo.', 'error'),
      });
  }

  private limparSelecao(): void {
    this.termoSelecionadoId.set(null);
    this.termoDetalhe.set(null);
  }
}
